import { FLAT_RATE_PLANS } from "@common/flatRateBilling";
import type { FeaturesSection } from "./plansTypes";

export const INVOICING_SECTION_TITLE = "Invoicing";

export const detailedFeaturesSections = [
    {
        title: "Bookkeeping",
        features: [
            "Auto categorization",
            "Cash insights",
            "Insights email",
            "Custom categories",
            "Transfer matching",
            "Counterparties",
            "Splits",
            "Rules",
            "Rule groups",
            "Suggested rules",
            "Receipt matching",
            "Document matching",
            "Universal importer",
            "Statement sync",
            "Document processing",
            "Classes",
            "Departments",
            "Locations",
            "Projects",
            "Services",
            "Products",
        ],
    },
    {
        title: INVOICING_SECTION_TITLE,
        features: [
            "Create and send invoices",
            "Manage customers",
            "Manage items",
            "Schedule invoices",
            "Branded invoices",
        ],
    },
    {
        title: "Reporting",
        features: [
            "Cash ledger",
            "Profit & loss",
            "Balance sheet",
            "Trial balance",
            "Expenses by vendor",
            "Year-end tax package",
            "AR aging summary",
            "AP aging summary",
            "Custom reporting",
        ],
    },
    {
        title: "Accounting",
        features: [
            "Custom chart of accounts",
            "Journal entry editor",
            "Journal entry automation",
            "Cash-basis ledger",
            "Accrual-basis ledger",
            "Revenue recognition",
            "Multi-book",
            "Consolidation",
        ],
    },
    {
        title: "Close management",
        features: [
            "Task management",
            "Payments reconciliation",
            "Bank reconciliation",
            "Payroll reconciliation",
        ],
    },
    {
        title: "Support",
        features: ["Service level", "Migration", "Training", "Onboarding"],
    },
    {
        title: "Admin & security",
        features: ["Members", "Security", "Activity", "Permissions"],
    },
    {
        title: "Integrations",
        features: ["Plaid", "Stripe", "PayPal", "Mercury", "Ramp", "Gusto"],
    },
    {
        title: "Agents",
        features: ["CLI", "MCP"],
    },
] as const satisfies FeaturesSection[];

export type DetailedFeatures =
    (typeof detailedFeaturesSections)[number]["features"][number];

export const highlightsPerPlan = {
    [FLAT_RATE_PLANS.FREE]: [
        "Auto categorization",
        "Receipt matching",
        "Profit & loss",
        "Balance sheet",
    ],
    [FLAT_RATE_PLANS.BASIC]: [
        "MCP/CLI",
        "Universal importer",
        "Reconciliation",
        "Statement sync",
    ],
    [FLAT_RATE_PLANS.PLUS]: [
        "Multi-entity",
        "Classes",
        "AR/AP automation",
        "Accrual ledger",
    ],
    [FLAT_RATE_PLANS.ADVANCED]: [
        "Consolidation",
        "Revenue recognition",
        "Multi-book",
    ],
    [FLAT_RATE_PLANS.ENTERPRISE]: [
        "Migration & training",
        "Dedicated support",
        "Consolidation",
        "Deduction optimization",
    ],
    [FLAT_RATE_PLANS.FRESHBOOKS]: [
        "Migration & training",
        "Dedicated support",
        "Consolidation",
        "Deduction optimization",
    ],
    // Read-only mirrors Plus (D14); never shown in the self-serve plan picker.
    [FLAT_RATE_PLANS.READ_ONLY]: [
        "Multi-entity",
        "Classes",
        "AR/AP automation",
        "Accrual ledger",
    ],
    // Tax mirrors Basic; never shown in the self-serve plan picker.
    [FLAT_RATE_PLANS.TAX]: [
        "MCP/CLI",
        "Universal importer",
        "Reconciliation",
        "Statement sync",
    ],
} as const satisfies Record<FLAT_RATE_PLANS, string[]>;

export type HighlightFeatures =
    (typeof highlightsPerPlan)[FLAT_RATE_PLANS][number];

export const isBetaPlanFeature = (
    feature: HighlightFeatures | DetailedFeatures,
): boolean => feature === "Revenue recognition" || feature === "Multi-book";

const detailedFeaturesByPlanBase = {
    [FLAT_RATE_PLANS.FREE]: {
        // Bookkeeping
        "Auto categorization": true,
        "Cash insights": true,
        "Transfer matching": true,
        Counterparties: true,
        Splits: true,
        Rules: true,
        "Rule groups": true,
        "Suggested rules": true,
        "Universal importer": false,
        "Document matching": false,
        "Receipt matching": true,
        "Document processing": false,
        "Statement sync": false,
        Classes: false,
        Departments: false,
        Locations: false,
        Products: false,
        Projects: false,
        Services: false,

        // Invoicing
        "Create and send invoices": true,
        "Manage customers": true,
        "Manage items": true,
        "Schedule invoices": false,
        "Branded invoices": true,

        // Accounting
        "Cash ledger": true,
        "Custom reporting": false,
        "Profit & loss": true,
        "Balance sheet": true,
        "Trial balance": true,
        "Expenses by vendor": true,
        "Year-end tax package": true,
        "Journal entry editor": true,
        "Journal entry automation": true,
        "AR aging summary": false,
        "AP aging summary": false,
        "Bank reconciliation": false,
        "Payments reconciliation": true,
        "Payroll reconciliation": false,
        "Cash-basis ledger": true,
        "Accrual-basis ledger": false,
        "Revenue recognition": false,
        "Multi-book": false,
        "Custom chart of accounts": true,
        "Task management": true,

        // Support
        "Service level": "Self serve",
        Onboarding: false,
        Migration: "Self serve",
        Training: false,

        // Admin & security
        Members: true,
        Security: true,
        Permissions: true,
        Activity: true,

        // Insights
        "Insights email": true,
        "Custom categories": true,

        // Integrations
        Plaid: true,
        Stripe: true,
        PayPal: true,
        Mercury: true,
        Gusto: false,
        Ramp: true,

        // Agents
        CLI: false,
        MCP: false,

        // Early access
        Consolidation: false,
    },
    [FLAT_RATE_PLANS.BASIC]: {
        // Bookkeeping
        "Auto categorization": true,
        "Transfer matching": true,
        Counterparties: true,
        Splits: true,
        Rules: true,
        "Rule groups": true,
        "Suggested rules": true,
        "Universal importer": true,
        "Document matching": true,
        "Receipt matching": true,
        "Document processing": true,
        "Statement sync": true,
        Classes: false,
        Departments: false,
        Locations: false,
        Projects: false,
        Products: false,
        Services: false,

        // Invoicing
        "Create and send invoices": true,
        "Manage customers": true,
        "Manage items": true,
        "Schedule invoices": false,
        "Branded invoices": true,

        // Accounting
        "Cash ledger": true,
        "Custom reporting": false,
        "Profit & loss": true,
        "Balance sheet": true,
        "Trial balance": true,
        "Expenses by vendor": true,
        "Year-end tax package": true,
        "Journal entry editor": true,
        "Journal entry automation": true,
        "AR aging summary": false,
        "AP aging summary": false,
        "Bank reconciliation": true,
        "Payments reconciliation": true,
        "Payroll reconciliation": true,
        "Cash-basis ledger": true,
        "Accrual-basis ledger": false,
        "Revenue recognition": false,
        "Multi-book": false,
        "Custom chart of accounts": true,
        "Task management": true,

        // Support
        "Service level": "Standard",
        Onboarding: false,
        Migration: "Self serve",
        Training: "Group",

        // Admin & security
        Members: true,
        Security: true,
        Permissions: true,
        Activity: true,

        // Insights
        "Cash insights": true,
        "Insights email": true,
        "Custom categories": true,

        // Integrations
        Plaid: true,
        Stripe: true,
        PayPal: true,
        Mercury: true,
        Gusto: true,
        Ramp: true,

        // Agents
        CLI: true,
        MCP: true,

        // Early access
        Consolidation: false,
    },
    [FLAT_RATE_PLANS.PLUS]: {
        // Bookkeeping
        "Auto categorization": true,
        "Transfer matching": true,
        Counterparties: true,
        Splits: true,
        Rules: true,
        "Rule groups": true,
        "Suggested rules": true,
        "Universal importer": true,
        "Document matching": true,
        "Receipt matching": true,
        "Document processing": true,
        "Statement sync": true,
        Classes: "Unlimited",
        Departments: "Unlimited",
        Locations: "Unlimited",
        Projects: "Unlimited",
        Products: "Unlimited",
        Services: "Unlimited",

        // Invoicing
        "Create and send invoices": true,
        "Manage customers": true,
        "Manage items": true,
        "Schedule invoices": false,
        "Branded invoices": true,

        // Accounting
        "Cash ledger": true,
        "Custom reporting": true,
        "Profit & loss": true,
        "Balance sheet": true,
        "Trial balance": true,
        "Expenses by vendor": true,
        "Year-end tax package": true,
        "Journal entry editor": true,
        "Journal entry automation": true,
        "AR aging summary": true,
        "AP aging summary": true,
        "Bank reconciliation": true,
        "Payments reconciliation": true,
        "Payroll reconciliation": true,
        "Cash-basis ledger": true,
        "Accrual-basis ledger": true,
        "Revenue recognition": false,
        "Multi-book": false,
        "Custom chart of accounts": true,
        "Task management": true,

        // Support
        "Service level": "Prioritized",
        Onboarding: false,
        Migration: "Self serve",
        Training: "Group",

        // Admin & security
        Members: true,
        Security: true,
        Permissions: true,
        Activity: true,

        // Insights
        "Cash insights": true,
        "Insights email": true,
        "Custom categories": true,

        // Integrations
        Plaid: true,
        Stripe: true,
        PayPal: true,
        Mercury: true,
        Gusto: true,
        Ramp: true,

        // Agents
        CLI: true,
        MCP: true,

        // Early access
        Consolidation: false,
    },
    [FLAT_RATE_PLANS.ADVANCED]: {
        // Bookkeeping
        "Auto categorization": true,
        "Transfer matching": true,
        Counterparties: true,
        Splits: true,
        Rules: true,
        "Rule groups": true,
        "Suggested rules": true,
        "Universal importer": true,
        "Document matching": true,
        "Receipt matching": true,
        "Document processing": true,
        "Statement sync": true,
        Classes: "Unlimited",
        Departments: "Unlimited",
        Locations: "Unlimited",
        Projects: "Unlimited",
        Products: "Unlimited",
        Services: "Unlimited",

        // Invoicing
        "Create and send invoices": true,
        "Manage customers": true,
        "Manage items": true,
        "Schedule invoices": true,
        "Branded invoices": true,

        // Accounting
        "Cash ledger": true,
        "Custom reporting": true,
        "Profit & loss": true,
        "Balance sheet": true,
        "Trial balance": true,
        "Expenses by vendor": true,
        "Year-end tax package": true,
        "Journal entry editor": true,
        "Journal entry automation": true,
        "AR aging summary": true,
        "AP aging summary": true,
        "Bank reconciliation": true,
        "Payments reconciliation": true,
        "Payroll reconciliation": true,
        "Cash-basis ledger": true,
        "Accrual-basis ledger": true,
        "Revenue recognition": true,
        "Multi-book": true,
        "Custom chart of accounts": true,
        "Task management": true,

        // Support
        "Service level": "Prioritized",
        Onboarding: false,
        Migration: "Self serve",
        Training: "Group",

        // Admin & security
        Members: true,
        Security: true,
        Permissions: true,
        Activity: true,

        // Insights
        "Cash insights": true,
        "Insights email": true,
        "Custom categories": true,

        // Integrations
        Plaid: true,
        Stripe: true,
        PayPal: true,
        Mercury: true,
        Gusto: true,
        Ramp: true,

        // Agents
        CLI: true,
        MCP: true,

        // Early access
        Consolidation: true,
    },
    [FLAT_RATE_PLANS.ENTERPRISE]: {
        // Bookkeeping
        "Auto categorization": true,
        "Transfer matching": true,
        Counterparties: true,
        Splits: true,
        Rules: true,
        "Rule groups": true,
        "Suggested rules": true,
        "Universal importer": true,
        "Document matching": true,
        "Receipt matching": true,
        "Document processing": true,
        "Statement sync": true,
        Classes: "Unlimited",
        Departments: "Unlimited",
        Locations: "Unlimited",
        Projects: "Unlimited",
        Products: "Unlimited",
        Services: "Unlimited",

        // Invoicing
        "Create and send invoices": true,
        "Manage customers": true,
        "Manage items": true,
        "Schedule invoices": true,
        "Branded invoices": true,

        // Accounting
        "Cash ledger": true,
        "Custom reporting": true,
        "Profit & loss": true,
        "Balance sheet": true,
        "Trial balance": true,
        "Expenses by vendor": true,
        "Year-end tax package": true,
        "Journal entry editor": true,
        "Journal entry automation": true,
        "AR aging summary": true,
        "AP aging summary": true,
        "Bank reconciliation": true,
        "Payments reconciliation": true,
        "Payroll reconciliation": true,
        "Cash-basis ledger": true,
        "Accrual-basis ledger": true,
        "Revenue recognition": true,
        "Multi-book": true,
        "Custom chart of accounts": true,
        "Task management": true,

        // Support
        "Service level": "Dedicated",
        Onboarding: "Dedicated",
        Migration: "Dedicated",
        Training: "Dedicated",

        // Admin & security
        Members: true,
        Security: true,
        Permissions: true,
        Activity: true,

        // Insights
        "Cash insights": true,
        "Insights email": true,
        "Custom categories": true,

        // Integrations
        Plaid: true,
        Stripe: true,
        PayPal: true,
        Mercury: true,
        Gusto: true,
        Ramp: true,

        // Agents
        CLI: true,
        MCP: true,

        // Early access
        Consolidation: true,
    },
    [FLAT_RATE_PLANS.FRESHBOOKS]: {
        // Bookkeeping
        "Auto categorization": true,
        "Transfer matching": true,
        Counterparties: true,
        Splits: true,
        Rules: true,
        "Rule groups": true,
        "Suggested rules": true,
        "Universal importer": true,
        "Document matching": true,
        "Receipt matching": true,
        "Document processing": true,
        "Statement sync": true,
        Classes: "Unlimited",
        Departments: "Unlimited",
        Locations: "Unlimited",
        Projects: "Unlimited",
        Products: "Unlimited",
        Services: "Unlimited",

        // Invoicing
        "Create and send invoices": false,
        "Manage customers": false,
        "Manage items": false,
        "Schedule invoices": false,
        "Branded invoices": false,

        // Accounting
        "Cash ledger": true,
        "Custom reporting": true,
        "Profit & loss": true,
        "Balance sheet": true,
        "Trial balance": true,
        "Expenses by vendor": true,
        "Year-end tax package": true,
        "Journal entry editor": true,
        "Journal entry automation": true,
        "AR aging summary": true,
        "AP aging summary": true,
        "Bank reconciliation": true,
        "Payments reconciliation": true,
        "Payroll reconciliation": true,
        "Cash-basis ledger": true,
        "Accrual-basis ledger": true,
        "Revenue recognition": false,
        "Multi-book": false,
        "Custom chart of accounts": true,
        "Task management": true,

        // Support
        "Service level": "Dedicated",
        Onboarding: "Dedicated",
        Migration: "Dedicated",
        Training: "Dedicated",

        // Admin & security
        Members: true,
        Security: true,
        Permissions: true,
        Activity: true,

        // Insights
        "Cash insights": true,
        "Insights email": true,
        "Custom categories": true,

        // Integrations
        Plaid: true,
        Stripe: true,
        PayPal: true,
        Mercury: true,
        Gusto: true,
        Ramp: true,

        // Agents
        CLI: true,
        MCP: true,

        // Early access
        Consolidation: false,
    },
} satisfies Record<
    Exclude<FLAT_RATE_PLANS, FLAT_RATE_PLANS.READ_ONLY | FLAT_RATE_PLANS.TAX>,
    Record<DetailedFeatures, string | boolean>
>;

export const detailedFeaturesByPlan: Record<
    FLAT_RATE_PLANS,
    Record<DetailedFeatures, string | boolean>
> = {
    ...detailedFeaturesByPlanBase,
    // Read-only mirrors Plus (D14); never shown in the self-serve plan picker.
    // A copied object, not a shared reference, so a later edit to one plan
    // cannot leak into the other.
    [FLAT_RATE_PLANS.READ_ONLY]: {
        ...detailedFeaturesByPlanBase[FLAT_RATE_PLANS.PLUS],
    },
    // Tax mirrors Basic; never shown in the self-serve plan picker.
    [FLAT_RATE_PLANS.TAX]: {
        ...detailedFeaturesByPlanBase[FLAT_RATE_PLANS.BASIC],
    },
};

export const featureDescriptions: Record<DetailedFeatures, string | null> = {
    // Bookkeeping
    "Auto categorization":
        "Business transactions are categorized in real time with suggested categories",
    "Transfer matching":
        "Transfers between accounts and entities are detected and matched automatically",
    Counterparties:
        "Vendors and customers are identified automatically and assigned to transactions on your behalf",
    Splits: "Split any transaction across category, counterparty, class, or entity by amount or %",
    Rules: "Create multi-condition, multi-action rules across entities to automate complex transaction updates",
    "Rule groups":
        "Organize and prioritize rules by custom groups to control how transactions are updated",
    "Suggested rules":
        "Personalize your categorization by confirming suggested rules",
    "Universal importer":
        "Automatically import transactions from Bank and Credit Card Statements (PDF, CSV)",
    "Document matching":
        "Automatically match invoices, and bills when uploaded or sent by an approved phone number or email",
    "Receipt matching":
        "Automatically match receipts to transactionswhen uploaded or sent by an approved phone number or email",
    "Document processing":
        "Kick scans and categorizes uploaded files, keeping your business documents organized and searchable",
    Classes:
        "Create unlimited classes and subclasses to track profitability across any custom dimension",
    Departments:
        "See costs and performance broken down by department to assess profitability",
    Locations:
        "See costs and performance broken down by location to assess profitability",
    Projects:
        "See costs and performance broken down by project to assess profitability",
    Services:
        "See costs and performance broken down by service to assess profitability",
    Products:
        "See revenue and expenses broken down by product line to assess profitability",

    "Statement sync":
        "Kick automatically pulls bank and credit card statements from supported financial institutions",
    "Create and send invoices": null,
    "Manage customers": null,
    "Manage items": "Customize your items, services and pricing",
    "Schedule invoices": "Schedule and send invoices for future dates",
    "Branded invoices": "Add your company logo and your own color theme",

    // Reporting
    "Cash ledger":
        "A complete record of financial activity across all accounts and entities",
    "Custom reporting":
        "Create and save custom report views to see how your business is performing",
    "Task management":
        "Use Kick's automatically generated task list to close your books across accounts and entities each month",
    // Accounting
    "Profit & loss":
        "Assess your profitability by tracking your income and expenses over any period of time",
    "Balance sheet":
        "Get a clear snapshot of your financial position broken down by assets, liabilities and equity",
    "Trial balance":
        "Generate a trial balance to ensure debits match credits and your accounts balance",
    "Expenses by vendor":
        "Track how much you're spending by vendor over any period",
    "Year-end tax package":
        "One-click download with all the reports you or your CPA need to file taxes at year-end",
    "Journal entry editor":
        "Create manual journal entries on one or multiple ledgers",
    "Journal entry automation":
        "Schedule recurring and reversing journal entries to run automatically with custom conditions",
    "AR aging summary":
        "Breakdown of outstanding invoices by age, so you can see what's overdue and prioritize collections",
    "AP aging summary":
        "Breakdown of outstanding bills by age, so you can see what's due and manage payments",
    "Bank reconciliation":
        "Assisted transaction-level reconciliation of bank and credit card activity to close each period with confidence",
    "Payments reconciliation":
        "Payment transactions sync as splits, so revenue and fees are accurately booked",
    "Payroll reconciliation":
        "When connected to Gusto, payroll transactions are automatically split and mapped to the correct expense accounts",
    "Cash-basis ledger":
        "See your finances based on when money actually hits or leaves your accounts",
    "Accrual-basis ledger":
        "See your finances based on when revenue is earned and expenses are incurred",
    "Revenue recognition":
        "Recognize revenue as it's earned, instead of when cash is collected",
    "Multi-book":
        "Create custom ledgers to run multiple accounting and reporting methods side-by-side",
    "Custom chart of accounts":
        "Customize your Chart of Accounts to match your business structure and reporting needs",

    // Support
    "Service level":
        "Get email and chat support, with priority and dedicated help on higher plans",
    Onboarding: "Get personalized CPA-led onboarding to set up your workspace",
    Migration:
        "Get CPA-guided migration help when switching from your current accounting software",
    Training:
        "Get CPA-led training for your team, with dedicated sessions on Enterprise",

    // Admin & security
    Members:
        "Invite your members, team, accountant and partners at no extra cost",
    Security: "Track login time, location and IP for every workspace sign-in",
    Permissions:
        "Control who can view and edit your books with role-based permissions",
    Activity:
        "Track workspace activity by member across all your business entities",

    // Insights
    "Cash insights":
        "Get a real-time view of cash coming in and going out across your entities",
    "Insights email": "Get a weekly and monthly email with financial summaries",
    "Custom categories":
        "Create unlimited custom categories that match how you run your business",

    // Integrations
    Plaid: "Connect to Plaid to sync transactions from 10,000+ bank accounts and credit cards",
    Stripe: "Directly connect to Stripe to sync payments, products, customers, fees, payouts, and more",
    PayPal: "Directly connect to PayPal to sync payments, products, customers, fees, payouts, expenses, and more",
    Mercury:
        "Directly connect to Mercury to sync bank, credit card, and treasury transactions, account statements, invoices, and customers",
    Gusto: "Directly connect to Gusto to sync your payroll data so you never need to manually reconcile again",
    Ramp: "Directly connect to Ramp to sync credit card transactions, reimbursements, bills, and vendors to automate AP reconciliation and more",

    // Agents
    CLI: "Automate financial workflows and minimize your token spend from the command line",
    MCP: "Directly connect your Claude, ChatGPT or Cursor using Model Context Protocol (MCP)",

    // Early access
    Consolidation:
        "Real-time multi-entity consolidated financials and eliminations",
};
